'use client';
import { getContentImageProps } from '@/lib/utils';
import dynamic from 'next/dynamic';
import { useState, useLayoutEffect } from 'react';
// Avoid SSR for ReactPlayer to avoid hydration issues
// https://github.com/cookpete/react-player/issues/1428
const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });
import { SvgPlay } from '@/components/Icons/SvgPlay';
import { SvgPause } from '@/components/Icons/SvgPause';
import Image from 'next/image';
import { revealed } from '@/lib/animation/revealed';

export default function FullWidthFeature({ block, position }) {
  const {
    uid,
    type,
    image,
    video,
    animations_enabled = false
  } = block.full_width_feature.reference[0];
  const animateComponent = animations_enabled;

  return (
    <section id={`${uid}_${position}`} data-name={'full-width-feature'}>
      <revealed.div
        className="relative h-full w-full overflow-hidden lg:my-5 lg:px-5"
        animate={animateComponent}
        fade
        distance="24rem"
      >
        {type == 'Image' ? (
          <ImageFeature data={{ ...image, animateComponent }} />
        ) : type == 'Video' ? (
          <VideoFeature data={{ ...video, uid, position, animateComponent }} />
        ) : null}
      </revealed.div>
    </section>
  );
}

function ImageFeature({ data }) {
  const {
    desktop_image,
    mobile_image,
    tablet_image,
    center_text,
    light_text,
    heading,
    description,
    animateComponent
  } = data;

  const { srcSet: desktopSrcSet } = getContentImageProps({
    image: desktop_image,
    sizes: '100vw'
  });
  const { srcSet: tabletSrcSet } = getContentImageProps({
    image: tablet_image,
    sizes: '100vw'
  });
  const { srcSet: mobileSrcSet, remainingProps } = getContentImageProps({
    image: mobile_image,
    sizes: '100vw'
  });

  return (
    <>
      <revealed.picture animate={animateComponent}>
        <source media="(max-width: 639px)" srcSet={mobileSrcSet} />
        <source media="(max-width: 1024px)" srcSet={tabletSrcSet} />
        <source srcSet={desktopSrcSet} />
        <img
          {...remainingProps}
          className={`block h-full w-full`}
          alt={desktop_image.title.replace(/\.[^/.]+$/, '')}
        />
      </revealed.picture>
      <revealed.div
        className="absolute top-0 right-0 w-full px-5 py-20 sm:px-15 lg:px-20"
        animate={animateComponent}
        fade
        distance="24rem"
      >
        <div
          className={`z-40 flex w-full ${center_text ? 'justify-center' : ''} ${light_text ? 'text-white' : ''}`}
        >
          <div
            className={`lg:max-w-110 ${center_text ? 'max-w-95 text-center' : 'max-w-70'} ${light_text ? 'text-white' : ''}`}
          >
            {heading && <h2 className="h3 sm:h2 lg:h1 mb-2.5">{heading}</h2>}
            {description && (
              <div
                className={`rte body-r lg:body-l`}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        </div>
      </revealed.div>
    </>
  );
}

function VideoFeature({ data }) {
  const { loop, placeholder_image, video_url, animateComponent } = data;
  const autoplay = true;
  const [videoPlaying, setVideoPlaying] = useState(!!autoplay);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise.
  useLayoutEffect(() => {
    return () => {
      setVideoPlaying(!!autoplay);
      setVideoLoaded(false);
      setRemountKey((key) => key + 1);
    };
  }, [autoplay]);

  //play or pause video on icon click
  function handleClick() {
    setVideoPlaying(!videoPlaying);
  }

  return (
    <>
      <Image
        src={placeholder_image.url}
        alt={placeholder_image.title}
        width={placeholder_image.dimension.width}
        height={placeholder_image.dimension.height}
        sizes="100vw"
        className={`block h-full w-full ${videoLoaded ? 'invisible' : ''}`}
      />
      <revealed.div
        className="absolute top-0 left-0 h-full w-full lg:px-5"
        animate={animateComponent}
        fade
        distance="24rem"
      >
        <ReactPlayer
          key={remountKey}
          url={video_url}
          loop={loop}
          playing={videoPlaying}
          playsinline={true}
          volume={0}
          muted={true}
          width="100%"
          height="100%"
          onBufferEnd={() => {
            setVideoLoaded(true);
          }}
          onEnded={() => setVideoPlaying(false)}
        />
        {videoLoaded && (
          <>
            {videoPlaying ? (
              <SvgPause
                className="absolute right-[14px] bottom-4 z-40 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px] lg:right-[26px]"
                handleClick={handleClick}
              />
            ) : (
              <SvgPlay
                className="absolute right-[14px] bottom-4 z-40 h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px] lg:right-[26px]"
                handleClick={handleClick}
              />
            )}
          </>
        )}
      </revealed.div>
    </>
  );
}
